import React, {useContext, useState} from "react";
import {createUseStyles, useTheme} from "react-jss";
import ParticipantCounter from "../common/ParticipantCounter";
import {Theme} from "engage-client/theme";
import Dashboard from "../dashboard/Dashboard";
import Leaderboard from "../leaderboard/Leaderboard";
import {
    AccessoryLgWidth,
    AccessoryXlWidth,
    ExpansionTimeMS,
    PanelState,
    ViewMyDashboardWidth
} from "./accessoryConstants";
import {EngageAppContext} from "../../App";
import {ExpandButton} from "./ExpandButton";
import {Role} from "engage-common/types";
import Chat from "../chat/Chat";
import {AccessoryTab} from "./AccessoryTab";
import {TriviaStatus} from "../../state/state";
import Tabs, {TabPanel} from "engage-client/components/Tabs";
import clsx from "clsx";
import {ChatContext, ChatUIState} from "../../contexts/ChatContext";
import GameComplete from "../common/GameComplete";

const ExpandedLeftMargin = 57;
const ExpandedRightMargin = 30;

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
    desktopPanel: {
        position: "absolute",
        bottom: 0,
        right: 0,
        top: 0,
        background: theme.accessoryPanel.background,
        transition: `width ${ExpansionTimeMS}ms ease-out`,
        [theme.breakpoints.only("lg")] : {
            width: ({panelState:ps, showLeaderboard}) => ps === PanelState.expanded || ps === PanelState.expanding ?
                `calc(100vw - ${ExpandedLeftMargin}px)` :
                AccessoryLgWidth + (showLeaderboard ? ViewMyDashboardWidth : 0),
        },
        [theme.breakpoints.only("xl")] : {
            width: ({panelState:ps, showLeaderboard}) => ps === PanelState.expanded || ps === PanelState.expanding ?
                `calc(100vw - ${ExpandedLeftMargin}px)` :
                AccessoryXlWidth + (showLeaderboard ? ViewMyDashboardWidth : 0),
        },
    },
    dashboardClipper: {
        position:"absolute",
        top: 0,
        bottom: 0,
        left: ViewMyDashboardWidth,
        overflow: "hidden",
        [theme.breakpoints.only("lg")] : {
            right: AccessoryLgWidth,
        },
        [theme.breakpoints.only("xl")] : {
            right: AccessoryXlWidth,
        },
    },
    dashboard: {
        position:"absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: `calc(100vw - ${AccessoryLgWidth + ViewMyDashboardWidth + ExpandedLeftMargin + ExpandedRightMargin}px)`,
        [theme.breakpoints.only("xl")] : {
            width: `calc(100vw - ${AccessoryXlWidth + ViewMyDashboardWidth + ExpandedLeftMargin + ExpandedRightMargin}px)`,
        }
    },
    leaderboard: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: AccessoryLgWidth,
        [theme.breakpoints.only("xl")] : {
            width: AccessoryXlWidth,
        },
        display: "flex",
        flexDirection: "column",
    },
    tabs: {
        background: theme.accessoryPanel.tabs.background,
        borderBottom: "none",
    },
    participantCount: {
        margin: "20px 0"
    },
    tabPanelContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        "&.leaderboard": {
        },
    },
}));

type DesktopAccessoryPanelProps = {
    toggleSlide:()=>void
    panelState: PanelState
    transitionEnd: React.TransitionEventHandler<HTMLDivElement>
    showAnswerWarning: boolean
    sharedDisplay: boolean
}

export default function DesktopAccessoryPanel({panelState, toggleSlide,transitionEnd, showAnswerWarning, sharedDisplay}:DesktopAccessoryPanelProps) {
    const theme:Theme = useTheme();

    const {user,appState} = useContext(EngageAppContext.current);
    const {chatReadCount, chatUIState} = useContext(ChatContext);
    const [hoveredUserID, setHoveredUserID] = useState<string|undefined>();
    const [leaderboardRowCount, setLeaderboardRowCount] = useState(0);
    const [openTab, setOpenTab] = React.useState(0);
    const showLeaderboard = appState.common.gameOptions.showLeaderboard;
    const unreadCount = chatUIState !== ChatUIState.focused ? appState.chat.entries.length - chatReadCount : 0;
    const handleChange = (newTab: number) => {
        setOpenTab(newTab);
    };

    const classes = useStyles({
        panelState,
        showLeaderboard
    });

    return <div className={classes.desktopPanel} onTransitionEnd={transitionEnd}>
        {appState.common.state === TriviaStatus.Complete && <GameComplete />}
        { user?.role !== Role.host && showLeaderboard && appState.common.state !== TriviaStatus.Complete &&
            <ExpandButton toggleSlide={toggleSlide} panelState={panelState} showAnswerWarning={showAnswerWarning}/>
        }
        { (panelState !== PanelState.collapsed && showLeaderboard) &&
                <div className={classes.dashboardClipper}>
                    <Dashboard className={classes.dashboard} hoveredUserID={hoveredUserID}
                               leaderboardRowCount={leaderboardRowCount}/>
                </div>
        }
        <div className={classes.leaderboard}>
            <ParticipantCounter className={classes.participantCount} showURL={sharedDisplay}/>
            { showLeaderboard && appState.common.gameOptions.showChat && !sharedDisplay ?
            <>
                <TabPanel value={openTab} index={0} className={clsx(classes.tabPanelContent, "leaderboard")}>
                    <Leaderboard setHoveredUserID={setHoveredUserID} setLeaderboardRowCount={setLeaderboardRowCount}/>
                </TabPanel>
                <TabPanel value={openTab} index={1}
                          className={clsx(classes.tabPanelContent, "chat")}>
                    <Chat/>
                </TabPanel>
                <Tabs value={openTab} onChange={handleChange} className={classes.tabs}>
                    <AccessoryTab label="Leaderboard" icon={theme.accessoryPanel.tabs.leaderboardIcon}/>
                    <AccessoryTab label="Chat" icon={theme.accessoryPanel.tabs.chatIcon} notificationCount={unreadCount} last={true}/>
                </Tabs>
            </>
                : showLeaderboard ?
                <Leaderboard setHoveredUserID={setHoveredUserID} setLeaderboardRowCount={setLeaderboardRowCount}/>
                :
                !sharedDisplay && <Chat/>
            }
        </div>
    </div>;

}